import * as _ from 'lodash-es';
import { BlockEmbed } from 'quill/blots/block';
import type Scroll from 'quill/blots/scroll';
import Quill, { Delta, Parchment } from 'quill/core';
import type { Range } from 'quill/core/selection';
import type { StackItem } from 'quill/modules/history';
import QuillHistory, { getLastChangeIndex } from 'quill/modules/history';
import {
  ACTION_POST_EDITOR_UNDO,
  CATEGORY_PRODUCT,
} from '../constants/analytics';
import { track } from '../utils/analytics';
import type { TableChange } from './table/types';

type SelectionRestorer = (opData: unknown, leaf: BlockEmbed) => boolean;

const blockEmbedSelectionRestorer: Record<string, SelectionRestorer> = {
  'table-embed': (opData, leaf) => {
    if ('getCellQuill' in leaf && typeof leaf.getCellQuill === 'function') {
      const cell = getContentChangedCellForOp(opData as TableChange);
      if (cell) {
        const quill = leaf.getCellQuill(cell.identity);
        if (quill) {
          quill.setSelection(
            getEditedRangeFromDelta(quill.scroll, new Delta(cell.content)),
            Quill.sources.USER,
          );
          return true;
        }
      }
    }

    return false;
  },
  'code-embed': (opData, leaf) => {
    if ('getQuill' in leaf && typeof leaf.getQuill === 'function') {
      const quill = leaf.getQuill();
      if (quill) {
        quill.setSelection(
          getEditedRangeFromDelta(
            quill.scroll,
            new Delta(opData as Delta['ops']),
          ),
          Quill.sources.USER,
        );
        return true;
      }
    }
    return false;
  },
};

class History extends QuillHistory {
  postIdForTrackingUndo?: string;

  squash(callback: () => void): void {
    const { lastRecorded } = this;

    this.lastRecorded = Date.now();
    callback();

    if (lastRecorded === 0) {
      this.lastRecorded = 0;
    }
  }

  undo() {
    super.undo();

    if (this.postIdForTrackingUndo) {
      track(ACTION_POST_EDITOR_UNDO, {
        category: CATEGORY_PRODUCT,
        label: this.postIdForTrackingUndo,
      });
    }
  }

  protected restoreSelection(stackItem: StackItem): void {
    const range =
      stackItem.range ??
      getEditedRangeFromDelta(this.quill.scroll, stackItem.delta);

    if (!this.tryRestoringBlockEmbedSelection(range, stackItem.delta)) {
      this.quill.setSelection(range, Quill.sources.USER);
    }
  }

  private tryRestoringBlockEmbedSelection(range: Range, delta: Delta): boolean {
    if (range.length > 1) return false;
    const leaf = this.quill.getLeaf(range.index)[0];
    const restorer =
      leaf instanceof BlockEmbed &&
      blockEmbedSelectionRestorer[leaf.statics.blotName];

    if (restorer) {
      const opData = getLastRetainOpData(delta);
      return opData ? restorer(opData, leaf) : false;
    }

    return false;
  }
}

function getFormatChange(delta: Delta): [null, null] | [number, number] {
  const lastOp = _.last(delta.ops);
  if (typeof lastOp?.retain === 'number' && lastOp.attributes) {
    if (delta.ops.length === 1) {
      return [0, lastOp.retain];
    }

    if (delta.ops.length === 2 && typeof delta.ops[0]?.retain === 'number') {
      return [delta.ops[0].retain, lastOp.retain];
    }
  }

  return [null, null];
}

function getEditedRangeFromDelta(scroll: Scroll, delta: Delta) {
  const [formattedIndex, formattedLength] = getFormatChange(delta);
  if (typeof formattedIndex === 'number') {
    return { index: formattedIndex, length: formattedLength };
  }

  const lastOp = _.last(delta.ops);
  const index = getLastChangeIndex(scroll, delta);

  const data = lastOp?.insert ?? lastOp?.retain;
  if (data && typeof data === 'object') {
    const key = Object.keys(data)[0];
    if (key && scroll.query(key, Parchment.Scope.BLOCK)) {
      return { index: index - 1, length: 1 };
    }
  }
  return { index, length: 0 };
}

function getLastRetainOpData(delta: Delta) {
  const lastOp = _.last(delta.ops);
  if (lastOp?.retain && typeof lastOp.retain === 'object') {
    const key = Object.keys(lastOp.retain)[0];
    return (key && lastOp.retain[key]) ?? null;
  }
  return null;
}

function getContentChangedCellForOp(
  tableChange: TableChange,
): { identity: string; content: Delta } | null {
  const { cells } = tableChange;
  if (!cells) return null;
  const identities = Object.keys(cells);

  for (let i = identities.length - 1; i >= 0; i -= 1) {
    const identity = identities[i];
    if (identity) {
      const cell = cells[identity];
      if (cell?.content) {
        return { identity, content: new Delta(cell.content) };
      }
    }
  }
  return null;
}

export default History;
